import { IShipmentRepository, CreateShipmentRequest, UpdateShipmentRequest, CreateShipmentMethodRequest, UpdateShipmentMethodRequest } from '@domain/purchasing/IShipmentRepository';
import { Shipment } from '@domain/shipment';
import { ShipmentMethod } from '@domain/shipment/models/ShipmentMethod';
import { Resource } from '@domain/base/Resource';
import { ShipmentDataSource } from '../datasources/ShipmentDataSource';
import { ShipmentMapper } from '../mappers/ShipmentMapper';
import { ShipmentMethodMapper } from '../mappers/ShipmentMethodMapper';

export class ShipmentRepository implements IShipmentRepository {
  constructor(private dataSource: ShipmentDataSource) { }

  async getShipments(): Promise<Resource<Shipment[]>> {
    try {
      const dtos = await this.dataSource.getShipments();
      const shipments = dtos.map(dto => ShipmentMapper.toModel(dto));
      return {
        status: 'success',
        data: shipments
      };
    } catch (error) {
      return {
        status: 'error',
        error: { type: 'UnknownError', message: 'Failed to fetch shipments' }
      };
    }
  }

  async getShipment(id: string): Promise<Resource<Shipment>> {
    try {
      const dto = await this.dataSource.getShipment(id);
      const shipment = ShipmentMapper.toModel(dto);
      return {
        status: 'success',
        data: shipment
      };
    } catch (error) {
      return {
        status: 'error',
        error: { type: 'UnknownError', message: 'Failed to fetch shipment' }
      };
    }
  }

  async createShipment(request: CreateShipmentRequest): Promise<Resource<Shipment>> {
    try {
      const dto = ShipmentMapper.createRequestToDTO(request);
      const responseDto = await this.dataSource.createShipment(dto);
      const shipment = ShipmentMapper.toModel(responseDto);
      return {
        status: 'success',
        data: shipment
      };
    } catch (error) {
      return {
        status: 'error',
        error: { type: 'UnknownError', message: 'Failed to create shipment' }
      };
    }
  }

  async updateShipment(id: string, request: Partial<UpdateShipmentRequest>): Promise<Resource<Shipment>> {
    try {
      const dto = ShipmentMapper.updateRequestToDTO(request);
      const responseDto = await this.dataSource.updateShipment(id, dto);
      const shipment = ShipmentMapper.toModel(responseDto);
      return {
        status: 'success',
        data: shipment
      };
    } catch (error) {
      return {
        status: 'error',
        error: { type: 'UnknownError', message: 'Failed to update shipment' }
      };
    }
  }

  async addPurchaseOrderItemsToShipment(id: string, request: { purchaseOrderItemIds: string[] }): Promise<Resource<void>> {
    try {
      await this.dataSource.addPurchaseOrderItems(id, request.purchaseOrderItemIds);
      return { status: 'success', data: undefined };
    } catch (error) {
      return {
        status: 'error',
        error: { type: 'UnknownError', message: 'Failed to add purchase order items to shipment' }
      };
    }
  }

  async removePurchaseOrderItemsFromShipment(id: string, request: { purchaseOrderItemIds: string[] }): Promise<Resource<void>> {
    try {
      await this.dataSource.removePurchaseOrderItems(id, request.purchaseOrderItemIds);
      return { status: 'success', data: undefined };
    } catch (error) {
      return {
        status: 'error',
        error: { type: 'UnknownError', message: 'Failed to remove purchase order items from shipment' }
      };
    }
  }

  async deleteShipment(id: string): Promise<Resource<void>> {
    try {
      await this.dataSource.deleteShipment(id);
      return { status: 'success', data: undefined };
    } catch (error) {
      return {
        status: 'error',
        error: { type: 'UnknownError', message: 'Failed to delete shipment' }
      };
    }
  }

  // Shipment Methods
  async getShipmentMethods(): Promise<Resource<ShipmentMethod[]>> {
    try {
      const dtos = await this.dataSource.getShipmentMethods();
      const shipmentMethods = dtos.map(dto => ShipmentMethodMapper.toModel(dto));
      return {
        status: 'success',
        data: shipmentMethods
      };
    } catch (error) {
      return {
        status: 'error',
        error: { type: 'UnknownError', message: 'Failed to fetch shipment methods' }
      };
    }
  }

  async getShipmentMethod(id: string): Promise<Resource<ShipmentMethod>> {
    try {
      const dto = await this.dataSource.getShipmentMethod(id);
      const shipmentMethod = ShipmentMethodMapper.toModel(dto);
      return {
        status: 'success',
        data: shipmentMethod
      };
    } catch (error) {
      return {
        status: 'error',
        error: { type: 'UnknownError', message: 'Failed to fetch shipment method' }
      };
    }
  }

  async createShipmentMethod(request: CreateShipmentMethodRequest): Promise<Resource<ShipmentMethod>> {
    try {
      const dto = {
        transportMethod: request.transportMethod as any,
        duration: request.duration,
        isDefault: request.isDefault,
        name: request.name,
      };
      const responseDto = await this.dataSource.createShipmentMethod(dto);
      const shipmentMethod = ShipmentMethodMapper.toModel(responseDto);
      return {
        status: 'success',
        data: shipmentMethod
      };
    } catch (error) {
      return {
        status: 'error',
        error: { type: 'UnknownError', message: 'Failed to create shipment method' }
      };
    }
  }

  async updateShipmentMethod(id: string, request: UpdateShipmentMethodRequest): Promise<Resource<ShipmentMethod>> {
    try {
      const dto = {
        transportMethod: request.transportMethod as any,
        duration: request.duration,
        name: request.name,
      };
      const responseDto = await this.dataSource.updateShipmentMethod(id, dto);
      const shipmentMethod = ShipmentMethodMapper.toModel(responseDto);
      return {
        status: 'success',
        data: shipmentMethod
      };
    } catch (error) {
      return {
        status: 'error',
        error: { type: 'UnknownError', message: 'Failed to update shipment method' }
      };
    }
  }

  async deleteShipmentMethod(id: string): Promise<Resource<void>> {
    try {
      await this.dataSource.deleteShipmentMethod(id);
      return { status: 'success', data: undefined };
    } catch (error) {
      return {
        status: 'error',
        error: { type: 'UnknownError', message: 'Failed to delete shipment method' }
      };
    }
  }
} 